import { useMemo, useState } from 'react';
import { StanBarChart, StanCard } from '@/components';
import { AnalyticsMetricCard } from './AnalyticsMetricCard';
import { useMultipleChartData } from '../queries/useAnalyticsQueries';
import IcCurrencyDollarCircle from '@/assets/icons/ic-currency-dollar-circle.svg';
import IcEyeShow from '@/assets/icons/ic-eye-show.svg';
import IcUser from '@/assets/icons/ic-user-02.svg';
import { useUser } from '@/queries/useUser';
import {
    calculateAnalyticsRange,
    getEndDateTimestamp,
    transformAnalyticsMetrics,
    transformChartValues,
} from '../logic/analytics.logic';
import { getCurrencySymbol } from '@/lib';
import { ANALYTICS_COLORS, CHART_CONFIG } from '../constants/analytics.constants';
import { type AnalyticsMetricsSectionProps, CHART_TYPES, type ChartType } from '../types/analytics.types';

export function AnalyticsMetricsSection({ dateRange, initialChart = CHART_TYPES.LEADS }: AnalyticsMetricsSectionProps) {
    const [activeChart, setActiveChart] = useState<ChartType>(initialChart);
    const { user } = useUser();

    const analyticsRange = calculateAnalyticsRange(dateRange);
    const endDateTimestamp = getEndDateTimestamp(dateRange.endDate);

    // Fetch analytics metrics data with transformation
    const chartTypes = useMemo(() => [CHART_TYPES.VIEWS, CHART_TYPES.REVENUE, CHART_TYPES.LEADS], []);
    const { data, isLoading } = useMultipleChartData(chartTypes, analyticsRange, endDateTimestamp);
    const transformedData = data ? transformAnalyticsMetrics(data) : null;

    const viewsData = transformedData?.views || null;
    const revenueData = transformedData?.revenue || null;
    const leadsData = transformedData?.leads || null;
    const loading = isLoading;

    const currencySymbol = getCurrencySymbol(user?.data?.currency);

    const viewsMetric = useMemo(
        () => ({
            title: 'Store Visits',
            value: viewsData?.total_current || 0,
            trend: viewsData?.ratio || 0,
            icon: IcEyeShow,
            color: ANALYTICS_COLORS.VIEWS,
        }),
        [viewsData?.total_current, viewsData?.ratio]
    );

    const revenueMetric = useMemo(
        () => ({
            title: 'Total Revenue',
            value: revenueData?.total_current || 0,
            trend: revenueData?.ratio || 0,
            icon: IcCurrencyDollarCircle,
            color: ANALYTICS_COLORS.REVENUE,
        }),
        [revenueData?.total_current, revenueData?.ratio]
    );

    const leadsMetric = useMemo(
        () => ({
            title: 'Leads',
            value: leadsData?.total_current || 0,
            trend: leadsData?.ratio || 0,
            icon: IcUser,
            color: ANALYTICS_COLORS.LEADS,
        }),
        [leadsData?.total_current, leadsData?.ratio]
    );

    const activeData = useMemo(() => {
        /**
         * Returns chart configuration and data for the currently active metric.
         * Includes color, currency prefix, and decimal settings specific to each chart type.
         */
        switch (activeChart) {
            case CHART_TYPES.VIEWS:
                return {
                    data: viewsData,
                    color: ANALYTICS_COLORS.VIEWS,
                    prefix: '',
                    decimals: 0,
                    name: 'Store Visits',
                };
            case CHART_TYPES.REVENUE:
                return {
                    data: revenueData,
                    color: ANALYTICS_COLORS.REVENUE,
                    prefix: currencySymbol,
                    decimals: 2,
                    name: 'Total Revenue',
                };
            case CHART_TYPES.LEADS:
                return {
                    data: leadsData,
                    color: ANALYTICS_COLORS.LEADS,
                    prefix: '',
                    decimals: 0,
                    name: 'Leads',
                };
            default:
                return {
                    data: leadsData,
                    color: ANALYTICS_COLORS.LEADS,
                    prefix: '',
                    decimals: 0,
                    name: 'Leads',
                };
        }
    }, [activeChart, viewsData, revenueData, leadsData, currencySymbol]);

    const series = useMemo(() => {
        if (!activeData?.data?.values || !activeData?.name) {
            return [];
        }

        try {
            return [
                {
                    name: activeData.name,
                    data: transformChartValues(activeData.data.values),
                },
            ];
        } catch (error) {
            console.warn('Error transforming chart values:', error);
            return [];
        }
    }, [activeData?.data?.values, activeData?.name]);

    const categories = useMemo(() => activeData?.data?.labels || [], [activeData?.data?.labels]);

    return (
        <StanCard className=''>
            <div className='flex w-full flex-wrap overflow-hidden lg:flex-nowrap lg:gap-6'>
                <div className='mt-4 w-1/2 lg:mt-0 lg:w-full'>
                    <AnalyticsMetricCard
                        metric={viewsMetric}
                        active={activeChart === CHART_TYPES.VIEWS}
                        loading={loading}
                        onClick={() => setActiveChart(CHART_TYPES.VIEWS)}
                        highlightColor={viewsMetric.color}
                    />
                </div>
                <div className='border-disabled order-first w-full border-b pb-4 lg:order-none lg:w-full lg:border-none lg:pb-0'>
                    <AnalyticsMetricCard
                        metric={revenueMetric}
                        active={activeChart === CHART_TYPES.REVENUE}
                        loading={loading}
                        keepStylingSameOnMobile
                        onClick={() => setActiveChart(CHART_TYPES.REVENUE)}
                        prefix={currencySymbol}
                        highlightColor={revenueMetric.color}
                    />
                </div>
                <div className='border-disabled mt-4 w-1/2 border-l pl-4 lg:mt-0 lg:w-full lg:border-none lg:pl-0'>
                    <AnalyticsMetricCard
                        metric={leadsMetric}
                        active={activeChart === CHART_TYPES.LEADS}
                        loading={loading}
                        onClick={() => setActiveChart(CHART_TYPES.LEADS)}
                        highlightColor={leadsMetric.color}
                    />
                </div>
            </div>

            <div className='w-full'>
                <StanBarChart
                    series={series}
                    categories={categories}
                    height={CHART_CONFIG.HEIGHT}
                    color={activeData?.color}
                />
            </div>
        </StanCard>
    );
}
